import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Card, Divider, Flex, Skeleton, Typography } from 'antd';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import Show from 'components/Show';
import TaskInfo from 'components/TaskInfo';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SeasonParticipant } from 'types/common';
import { renderDate } from 'utils/helpers';
import EditFarmerApdForm from './EditFarmerApdForm';

interface FarmerApdCardProps {
  participant: SeasonParticipant;
  taskAssignmentId: string;
}

const FarmerApdCard = ({
  participant,
  taskAssignmentId,
}: FarmerApdCardProps) => {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);

  return (
    <Card styles={{ body: { padding: 16 } }}>
      <Flex justify="space-between" align="center" className="mb-2">
        <Typography.Title level={5}>
          {participant?.farmer?.name || 'Unknown Farmer'}
        </Typography.Title>

        <ProtectedResource permission="mrv:edit">
          <Show
            when={!isEditing}
            fallback={
              <CloseOutlined
                onClick={() => setIsEditing(false)}
                style={{ color: '#256AF4', fontSize: 20 }}
                role="button"
              />
            }
          >
            <EditOutlined
              onClick={() => setIsEditing(true)}
              style={{ color: '#256AF4', fontSize: 20 }}
              role="button"
            />
          </Show>
        </ProtectedResource>
      </Flex>

      <Show
        when={!isEditing}
        fallback={
          <EditFarmerApdForm
            participant={participant}
            taskAssignmentId={taskAssignmentId}
            onEditSuccess={() => setIsEditing(false)}
          />
        }
      >
        <Flex wrap="wrap">
          <TaskInfo
            header={t('dashboard.actualPlantingDate')}
            value={renderDate(participant?.actualPlantingDate)}
          />

          <TaskInfo
            header={t('dashboard.recordedDate')}
            value={renderDate(participant?.reportedDate)}
          />
        </Flex>

        <Show when={!!participant?.lastModifiedBy}>
          <Flex vertical gap={8}>
            <Divider className="mt-4 mb-2" />
            <Flex wrap="wrap">
              <TaskInfo
                header={t('dashboard.lastModifiedDate')}
                value={renderDate(
                  participant?.lastModifiedDate,
                  '-',
                  'MMM DD, YYYY, HH:mm:ss',
                )}
              />

              <TaskInfo
                header={t('dashboard.lastModifiedBy')}
                value={participant?.lastModifiedBy}
              />
            </Flex>
          </Flex>
        </Show>
      </Show>
    </Card>
  );
};

export default FarmerApdCard;

FarmerApdCard.Skeleton = () => {
  return (
    <Card styles={{ body: { padding: 16 } }}>
      <Flex justify="space-between" align="center" className="mb-2">
        <Skeleton.Input active size="small" style={{ width: 180 }} />
        <Skeleton.Avatar active shape="circle" size="small" />
      </Flex>

      <Flex wrap="wrap" gap={32} justify="space-between" className="mt-4">
        <Skeleton.Input
          active
          size="small"
          style={{ width: 120, marginBottom: 4 }}
        />
        <Skeleton.Input active size="small" style={{ width: 100 }} />
      </Flex>
    </Card>
  );
};
